/* eslint-disable no-undef */
import { Theme, css } from '@emotion/react';

import Image from 'next/image';

import { useCallback, useMemo, useState } from 'react';

import { Radius, Typography } from 'chatie-design-system';

import { FullscreenModalProps } from '@components/common/FullscreenModal';
import Overlay from '@components/common/Overlay';

import { IEFinderObject } from '@model';
import { safeArea } from '@styles/css';

type Props = Omit<FullscreenModalProps, 'title'>;

const containerCSS = css`
  background-color: rgba(0, 0, 0, 0.8) !important;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  ${safeArea('top', 'padding')};
  ${safeArea('bottom', 'padding')};
`;

const imageContainerCSS = css`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: scroll;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const galleryContainerCSS = css`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  touch-action: pan-x;
`;

const galleryImageWrapCSS = css`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const counterCSS = (theme: Theme) => css`
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.6);
  color: ${theme.true.white};
  ${Typography.body14};
  padding: 4px 12px;
  border-radius: ${Radius.round}px;
  z-index: 2;
  pointer-events: none;
`;

const navButtonCSS = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 20px;
  z-index: 3;
  cursor: pointer;

  &:active {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const prevButtonCSS = css`
  ${navButtonCSS};
  left: 8px;
`;

const nextButtonCSS = css`
  ${navButtonCSS};
  right: 8px;
`;

type ImageModalProps = {
  /** 단일 이미지 (하위호환) */
  image?: IEFinderObject<'image'>;
  /** 멀티이미지 배열 */
  images?: IEFinderObject<'image'>[];
  /** 갤러리 시작 인덱스 */
  initialIndex?: number;
};

const ImageModal = ({
  image,
  images: imagesProp,
  initialIndex = 0,
  ...props
}: Props & ImageModalProps) => {
  const { onClose, open } = props;

  const images = useMemo(() => {
    if (imagesProp?.length) return imagesProp;
    if (image?.key) return [image];
    return [];
  }, [imagesProp, image]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // 모달이 열릴 때마다 initialIndex로 리셋
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }

  const currentImage = images[currentIndex];

  const handlePrev = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(
    // eslint-disable-next-line no-undef
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => Math.min(images.length - 1, prev + 1));
    },
    [images.length]
  );

  // 스와이프 핸들링
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX === null) return;
      const diff = e.changedTouches[0].clientX - touchStartX;
      const threshold = 50;

      if (diff > threshold && currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      } else if (diff < -threshold && currentIndex < images.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      }
      setTouchStartX(null);
    },
    [touchStartX, currentIndex, images.length]
  );

  if (!open || images.length === 0) {
    return <></>;
  }

  // 단일 이미지 모드
  if (images.length === 1 && currentImage) {
    return (
      <Overlay
        {...props}
        blocking={false}
        css={containerCSS}
        onClosePageClick={onClose}
      >
        <div css={imageContainerCSS}>
          <Image
            src={currentImage.attachment.url}
            alt={'post-image'}
            layout={'intrinsic'}
            loader={({ src }) => src}
            width={currentImage.attachment.width}
            height={currentImage.attachment.height}
            objectPosition={'center'}
            objectFit={'contain'}
          />
        </div>
      </Overlay>
    );
  }

  // 멀티이미지 갤러리 모드
  return (
    <Overlay
      {...props}
      blocking={false}
      css={containerCSS}
      onClosePageClick={onClose}
    >
      <div
        css={galleryContainerCSS}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {currentIndex > 0 && (
          <button css={prevButtonCSS} onClick={handlePrev}>
            ‹
          </button>
        )}

        <div css={galleryImageWrapCSS}>
          {currentImage && (
            <Image
              key={`modal-img-${currentIndex}`}
              src={currentImage.attachment.url}
              alt={`post-image-${currentIndex + 1}`}
              layout={'intrinsic'}
              loader={({ src }) => src}
              width={currentImage.attachment.width}
              height={currentImage.attachment.height}
              objectPosition={'center'}
              objectFit={'contain'}
            />
          )}
        </div>

        {currentIndex < images.length - 1 && (
          <button css={nextButtonCSS} onClick={handleNext}>
            ›
          </button>
        )}

        <div css={counterCSS}>
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </Overlay>
  );
};

export default ImageModal;
